import { UserSettings } from "../app/stores/user-settings.slice";
import { useIndexedDB } from "../hooks/use-indexed-db";
import { Application } from "../app/stores/applications-slice";
import { FileSystemItem } from "../app/stores/directory-slice";
import { useEffect, useState, createContext, useContext, useRef } from "react";
import { T } from "../components/basic/text";
import { LogEntry } from "../hooks/use-log-monitoring";
import { IDBPDatabase } from "idb";

interface IndexedDBContextType {
    db: IDBPDatabase | null;
    isInitialized: boolean;
    error: Error | null;
    logsDb: {
        data: LogEntry[];
        isLoading: boolean;
        error: Error | null;
        addItem: (item: LogEntry) => Promise<void>;
        updateItem: (item: LogEntry) => Promise<void>;
        deleteItem: (key: string | number) => Promise<void>;
        getItem: (key: string | number) => Promise<LogEntry | null>;
        getAllItems: () => Promise<LogEntry[]>;
    };
    userSettingsDb: {
        data: UserSettings[];
        isLoading: boolean;
        error: Error | null;
        addItem: (item: UserSettings) => Promise<void>;
        updateItem: (item: UserSettings) => Promise<void>;
        deleteItem: (key: string | number) => Promise<void>;
        getItem: (key: string | number) => Promise<UserSettings | null>;
        getAllItems: () => Promise<UserSettings[]>;
    };
    applicationsDb: {
        data: Application[];
        isLoading: boolean;
        error: Error | null;
        addItem: (item: Application) => Promise<void>;
        updateItem: (item: Application) => Promise<void>;
        deleteItem: (key: string | number) => Promise<void>;
        getItem: (key: string | number) => Promise<Application | null>;
        getAllItems: () => Promise<Application[]>;
    };
    directoriesDb: {
        data: FileSystemItem[];
        isLoading: boolean;
        error: Error | null;
        addItem: (item: FileSystemItem) => Promise<void>;
        updateItem: (item: FileSystemItem) => Promise<void>;
        deleteItem: (key: string | number) => Promise<void>;
        getItem: (key: string | number) => Promise<FileSystemItem | null>;
        getAllItems: () => Promise<FileSystemItem[]>;
    };
}

const IndexedDBContext = createContext<IndexedDBContextType>({
    db: null,
    isInitialized: false,
    error: null,
    logsDb: {
        data: [],
        isLoading: true,
        error: null,
        addItem: async () => {},
        updateItem: async () => {},
        deleteItem: async () => {},
        getItem: async () => null,
        getAllItems: async () => [],
    },
    userSettingsDb: {
        data: [],
        isLoading: true,
        error: null,
        addItem: async () => {},
        updateItem: async () => {},
        deleteItem: async () => {},
        getItem: async () => null,
        getAllItems: async () => [],
    },
    applicationsDb: {
        data: [],
        isLoading: true,
        error: null,
        addItem: async () => {},
        updateItem: async () => {},
        deleteItem: async () => {},
        getItem: async () => null,
        getAllItems: async () => [],
    },
    directoriesDb: {
        data: [],
        isLoading: true,
        error: null,
        addItem: async () => {},
        updateItem: async () => {},
        deleteItem: async () => {},
        getItem: async () => null,
        getAllItems: async () => [],
    },
});

export const useIndexedDBContext = () => useContext(IndexedDBContext);

export const IndexedDBProvider = ({ children }: { children: React.ReactNode }) => {
    const [isInitialized, setIsInitialized] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const initCheckRef = useRef(false);

    const logsDb = useIndexedDB<LogEntry>({
        version: 1,
        storeName: "logs",
        keyPath: "id",
        onError: (err) => setError(err.message),
    });

    const userSettingsDb = useIndexedDB<UserSettings>({
        version: 1,
        storeName: "userSettings",
        keyPath: "id",
        onError: (err) => setError(err.message),
    });

    const applicationsDb = useIndexedDB<Application>({
        version: 1,
        storeName: "applications",
        keyPath: "id",
        onError: (err) => setError(err.message),
    });

    const directoriesDb = useIndexedDB<FileSystemItem>({
        version: 1,
        storeName: "directories",
        keyPath: "id",
        onError: (err) => setError(err.message),
    });

    useEffect(() => {
        const checkInitialization = () => {
            if (isInitialized || initCheckRef.current) return;

            const loadingStates = {
                logs: logsDb.isLoading,
                userSettings: userSettingsDb.isLoading,
                applications: applicationsDb.isLoading,
                directories: directoriesDb.isLoading,
            };

            const allLoaded =
                !loadingStates.logs && !loadingStates.userSettings && !loadingStates.applications && !loadingStates.directories;

            if (allLoaded) {
                initCheckRef.current = true;
                setIsInitialized(true);
            }
        };

        // اجرای اولیه
        checkInitialization();

        // اجرای مجدد بعد از یک تأخیر کوتاه برای اطمینان از به‌روزرسانی وضعیت‌ها
        const timeoutId = setTimeout(checkInitialization, 100);

        return () => clearTimeout(timeoutId);
    }, [isInitialized, logsDb.isLoading, userSettingsDb.isLoading, applicationsDb.isLoading, directoriesDb.isLoading]);

    const contextValue: IndexedDBContextType = {
        db: logsDb.db || userSettingsDb.db || applicationsDb.db || directoriesDb.db,
        isInitialized,
        error: error ? new Error(error) : null,
        logsDb,
        userSettingsDb,
        applicationsDb,
        directoriesDb,
    };

    if (error) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="p-6 bg-white rounded-lg shadow-md">
                    <T as="h2" className="text-xl font-bold text-red-500 mb-2">
                        error-in-database-loading
                    </T>
                    <p className="text-gray-700">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors">
                        <T>try-again</T>
                    </button>
                </div>
            </div>
        );
    }

    if (!isInitialized) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-100">
                <div className="p-6 bg-white rounded-lg shadow-md">
                    <div className="flex items-center space-x-3">
                        <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                        <T as="p" className="text-gray-700">
                            loading-databases
                        </T>
                    </div>
                </div>
            </div>
        );
    }

    return <IndexedDBContext.Provider value={contextValue}>{children}</IndexedDBContext.Provider>;
};
